/**
 * BCE Anchor Scroll — native class anchors for every BCE element.
 *
 * On load, every element carrying a `bce_…` class gets a stable anchor id
 * derived from that class, so a button can link to it with a plain native
 * anchor — no volatile TYPO3 content id (#c50929) needed:
 *
 *   Element:  <div class="frame bce_branche-full" id="c50929">…</div>
 *   Button:   link/anchor  ->  #bce_branche-full
 *
 * The browser scrolls to it natively. If the same class appears several times
 * on a page, the anchors are numbered: bce_slider, bce_slider-2, bce_slider-3…
 *
 * The TYPO3 id stays: an element can only hold one id, so when it already has
 * one, BCE inserts a separate zero-height anchor element right before it.
 *
 * Global behavior — initialized once from index.js.
 *
 * @class BceAnchorScroll
 */
class BceAnchorScroll {
    constructor() {
        this.done = false;
    }

    init() {
        if (this.done) return;
        this.done = true;

        const counts = {};
        document.querySelectorAll('[class*="bce_"]').forEach((el) => {
            const base = this.baseClass(el);
            if (!base) return;

            counts[base] = (counts[base] || 0) + 1;
            const id = counts[base] === 1 ? base : `${base}-${counts[base]}`;
            if (document.getElementById(id)) return; // never clobber an existing id

            this.ensureAnchor(el, id);
        });

        // Deep link (/page#bce_branche-full): the browser tried before the id
        // existed, so jump once now that the anchors are in place.
        this.jumpToCurrentHash();
        window.addEventListener('hashchange', () => this.jumpToCurrentHash());
    }

    /**
     * First `bce_…` class on an element (the anchor name).
     * @param {HTMLElement} el
     * @returns {string|null}
     */
    baseClass(el) {
        for (const cls of el.classList) {
            if (cls.startsWith('bce_') && cls.length > 4) return cls;
        }
        return null;
    }

    /**
     * Give the element the id directly if it has none, otherwise inject a
     * separate anchor right before it (keeps the TYPO3 id intact).
     *
     * The anchor is `position:absolute` with no offsets: it is taken out of
     * flow — so it is never a grid/flex item and takes zero space — while its
     * static position stays at the element's top edge, so the scroll still
     * lands correctly.
     * @param {HTMLElement} el
     * @param {string} id
     */
    ensureAnchor(el, id) {
        if (!el.id) {
            el.id = id;
            return;
        }
        const anchor = document.createElement('span');
        anchor.id = id;
        anchor.setAttribute('aria-hidden', 'true');
        anchor.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
        el.parentNode.insertBefore(anchor, el);
    }

    jumpToCurrentHash() {
        const id = decodeURIComponent(window.location.hash.slice(1));
        if (!id) return;
        const target = document.getElementById(id);
        if (target) {
            setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        }
    }
}

export default new BceAnchorScroll();
